/*
 * Reveal-on-scroll for the Artist Grant page.
 *
 * Any element matching one of the selectors below fades in and slides up the
 * first time it scrolls into view. Elements that share a parent are staggered
 * so rows (e.g. the stats columns) cascade in. Purely decorative: if JS is off
 * or the user prefers reduced motion, everything is shown immediately.
 */
const REVEAL_SELECTORS = [
  '[id$="__stats_section"] .group-block .group-block',
  '[id$="__ready_to_apply"] .section-content-wrapper > .text-block',
  '[id$="__ready_to_apply"] .section-content-wrapper > .button',
];

const STAGGER_MS = 120;

function init() {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const targets = [];

  REVEAL_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (!targets.includes(el)) targets.push(el);
    });
  });

  if (targets.length === 0) return;

  if (reducedMotion || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('fv-reveal', 'fv-reveal--in'));
    return;
  }

  // Stagger each element by its position among its siblings.
  targets.forEach((el) => {
    el.classList.add('fv-reveal');
    const siblings = Array.from(el.parentElement?.children || []).filter((n) =>
      targets.includes(n)
    );
    const index = Math.max(0, siblings.indexOf(el));
    el.style.transitionDelay = `${index * STAGGER_MS}ms`;
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('fv-reveal--in');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
  );

  targets.forEach((el) => observer.observe(el));
}

/*
 * Scroll parallax for the "Who can apply" artwork: it drifts vertically and
 * gently rotates around its -5deg resting tilt as the section moves through the
 * viewport. Driven by a single rAF loop, only while the image is on screen.
 */
const PARALLAX_SELECTOR = '[id$="__who_can_apply"] .image-block';
const BASE_ROTATION = -5; // deg, matches the CSS resting tilt
const MAX_SHIFT = 36; // px of vertical drift at the extremes
const ROTATION_RANGE = 3; // deg of extra tilt either side of the base

function initParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const image = document.querySelector(PARALLAX_SELECTOR);
  if (!image) return;

  let onScreen = false;
  let ticking = false;

  const update = () => {
    ticking = false;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const rect = image.getBoundingClientRect();
    const center = rect.top + rect.height / 2;
    // -1 when entering from the bottom, +1 when leaving at the top.
    const progress = (center - vh / 2) / ((vh + rect.height) / 2);
    const clamped = Math.max(-1, Math.min(1, progress));

    image.style.setProperty('--fv-wca-shift', `${(-clamped * MAX_SHIFT).toFixed(2)}px`);
    image.style.setProperty('--fv-wca-rot', `${(BASE_ROTATION + clamped * ROTATION_RANGE).toFixed(2)}deg`);
  };

  const onScroll = () => {
    if (!onScreen || ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        onScreen = entries[0].isIntersecting;
        if (onScreen) onScroll();
      },
      { rootMargin: '10% 0px' }
    ).observe(image);
  } else {
    onScreen = true;
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
}

function boot() {
  init();
  initParallax();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
